/**
 * Gets the file extension from a filename
 */
export function getFileExtension(filename: string): string {
	const ext = filename?.split('.')?.pop();
	return ext ? `.${ext}` : '';
}

/**
 * Generates a standardized filename for downloads
 * Priority: <candidate-name>.<ext> -> originalFilename -> fallback
 */
export function getDownloadFilename({
	candidateName,
	originalFilename,
	fallback = 'document',
}: {
	candidateName: string;
	originalFilename: string;
	fallback?: string;
}): string {
	// If we have a candidate name, use it with the original file extension
	if (candidateName && originalFilename) {
		const ext = getFileExtension(originalFilename);
		return `${candidateName}${ext}`;
	}

	// Fall back to original filename
	if (originalFilename) {
		return originalFilename;
	}

	// Final fallback
	return fallback;
}

export function downloadFile({
	data,
	filename,
	fileType,
}: {
	data: string;
	filename: string;
	fileType: string;
}) {
	try {
		const blob = new Blob([data], { type: fileType });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		a.click();
		a.remove();
		setTimeout(() => {
			URL.revokeObjectURL(url);
		});
	} catch (error) {
		console.error(error);
	}
}

export function downloadFileFromUrl({
	url,
	filename,
	shouldCleanupUrl = true,
}: {
	url: string;
	filename: string;
	shouldCleanupUrl?: boolean;
}) {
	try {
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		a.click();
		a.remove();

		// Automatically cleanup blob URLs after download
		if (shouldCleanupUrl && url.startsWith('blob:')) {
			setTimeout(() => {
				URL.revokeObjectURL(url);
			}, 1000);
		}
	} catch (error) {
		console.error(error);
	}
}

export function sanitizeFileName(name: string): string {
	const ext = name.split('.').pop();
	const base = name
		.substring(0, name.lastIndexOf('.'))
		.replace(/[^a-zA-Z0-9_-]/g, '_')
		.replace(/_{2,}/g, '_')
		.replace(/^_|_$/g, '')
		.substring(0, 200);
	return `${base}.${ext}`;
}